import { startOfDay } from "date-fns";
import create from "zustand";
import { GetState, SetState } from "zustand";

import { SurveyModel, SurveyQuestionJson } from "@performyard/service-contracts";

import { SurveyDetailsModal, SurveyDetailsStore } from "./model";

const surveyDetailsStore = (set: SetState<SurveyDetailsStore>, get: GetState<SurveyDetailsStore>) => ({
	startDate: "" as const,
	endDate: "" as const,
	surveyStatus: null as SurveyModel["status"] | null,
	modal: null,
	publishFormErrors: {
		startDate: null,
		endDate: null,
	},
	validQuestions: [],
	validName: "",
	surveyLink: "",
	setters: {
		setStartDate: (startDate: Date | "") => set({ startDate }),
		setEndDate: (endDate: Date | "") => set({ endDate }),
		setSurveyStatus: (surveyStatus: SurveyModel["status"] | null) => set({ surveyStatus }),
		setModal: (modal: SurveyDetailsModal | null) => set({ modal }),
		setPublishFormErrors: (publishFormErrors: { startDate: string | null; endDate: string | null }) =>
			set({ publishFormErrors }),
		setValidQuestions: (validQuestions: SurveyQuestionJson[]) => set({ validQuestions }),
		setValidName: (validName: string) => set({ validName }),
		setSurveyLink: (surveyLink: string) => set({ surveyLink }),
	},
	actions: {
		validateSurveyDates: (onValid?: () => void) => {
			const { startDate, endDate, surveyStatus } = get();
			const now = startOfDay(new Date());
			const startDateSelected = startDate !== "";
			const endDateSelected = endDate !== "";

			const publishFormErrors = { ...get().publishFormErrors };
			if (!startDateSelected && ["scheduled", "draft"].includes(surveyStatus ?? "")) {
				publishFormErrors.startDate = "This is a required field";
			}
			if (startDateSelected && startDate < now && ["scheduled", "draft"].includes(surveyStatus ?? "")) {
				publishFormErrors.startDate = "Start date must be in the future";
			}
			if (endDateSelected && endDate < now) {
				publishFormErrors.endDate = "End date must be in the future";
			} else if (endDateSelected && endDate < startDate) {
				publishFormErrors.endDate = "End date must be after start date";
			}
			if (Object.values(publishFormErrors).some((error) => error !== null)) {
				set({ publishFormErrors });
				return false;
			}

			onValid && onValid();
			return true;
		},
	},
});

export const useSurveyDetailsStore = create<SurveyDetailsStore>(surveyDetailsStore);

export const useStartDate = () => useSurveyDetailsStore((state) => state.startDate);
export const useEndDate = () => useSurveyDetailsStore((state) => state.endDate);
export const useSurveyStatus = () => useSurveyDetailsStore((state) => state.surveyStatus);
export const useModal = () => useSurveyDetailsStore((state) => state.modal);
export const useValidQuestions = () => useSurveyDetailsStore((state) => state.validQuestions);
export const useValidName = () => useSurveyDetailsStore((state) => state.validName);
export const useSurveyLink = () => useSurveyDetailsStore((state) => state.surveyLink);

export const useSetters = () => useSurveyDetailsStore((state) => state.setters);
export const useActions = () => useSurveyDetailsStore((state) => state.actions);

export const usePublishFormErrors = () => useSurveyDetailsStore((state) => state.publishFormErrors);
